import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lightning, Desktop, Play, CircleNotch, Code, DownloadSimple, Copy, X, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { copyText } from "@/lib/clipboard";

const OS = [
  { k: "bat", l: "Windows (.bat)", file: "sliply-worker.bat", hint: "Double-click the file. Needs Python 3.10+ installed." },
  { k: "sh", l: "Mac / Linux (.sh)", file: "sliply-worker.sh", hint: "Run: bash sliply-worker.sh" },
  { k: "docker", l: "Docker", file: "docker-compose.yml", hint: "Follow the 3 steps at the top of the file, then docker compose up -d --build" },
];

function ScriptDialog({ selected, all, onClose, ingest }) {
  const [picked, setPicked] = useState(selected.length ? selected : all.map((r) => r.key));
  const [kind, setKind] = useState("bat");
  const [basket, setBasket] = useState(true);
  const [schedule, setSchedule] = useState(true);
  const [parallel, setParallel] = useState(3);
  const qs = `kind=${kind}&retailers=${picked.join(",")}&basket=${basket}&schedule=${schedule}&parallel=${parallel}`;
  const os = OS.find((o) => o.k === kind);
  const args = `--retailer ${picked.join(",")}${basket ? " --basket --limit 500" : ""} --parallel ${parallel}${schedule ? " --schedule" : ""}`;
  const cmd = kind === "bat"
    ? `set SCRAPER_INGEST_URL=${ingest?.ingest_url}\nset SCRAPER_INGEST_TOKEN=${ingest?.token}\npython worker.py ${args}`
    : `SCRAPER_INGEST_URL=${ingest?.ingest_url} SCRAPER_INGEST_TOKEN=${ingest?.token} python worker.py ${args}`;
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const dl = () => downloadFile(`/admin/scraper/script?${qs}`, os.file).then(() => toast.success(`${os.file} downloaded`)).catch(() => toast.error("Could not generate script"));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="script-dialog" className="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <button data-testid="script-dialog-close" onClick={onClose} className="float-right text-muted-foreground hover:text-neutral-900"><X size={20} /></button>
        <h3 className="font-head text-xl font-bold flex items-center gap-2"><Code size={22} className="text-riq-primary" /> Generate worker script</h3>
        <p className="text-xs text-muted-foreground mt-1">For retailers that block cloud servers. Run it on any home or office PC; it uploads prices to {ingest?.ingest_url} with your ingest key pre-filled.</p>

        <div className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Retailers</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {all.map((r) => (
            <button key={r.key} data-testid={`script-retailer-${r.key}`} onClick={() => toggle(r.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${picked.includes(r.key) ? "bg-riq-primary text-white" : "border border-riq-border hover:border-riq-primary"}`}>{r.name}</button>
          ))}
        </div>

        <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
          <label className="flex items-center gap-2"><input data-testid="script-opt-basket" type="checkbox" checked={basket} onChange={(e) => setBasket(e.target.checked)} /> HPI basket (500 items)</label>
          <label className="flex items-center gap-2"><input data-testid="script-opt-schedule" type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} /> Monthly on the 20th</label>
          <label className="flex items-center gap-2">At once
            <select data-testid="script-opt-parallel" value={parallel} onChange={(e) => setParallel(+e.target.value)} className="rounded-lg border border-riq-border px-2 py-1">{[1, 2, 3, 4].map((n) => <option key={n}>{n}</option>)}</select>
          </label>
        </div>

        <div className="mt-5 inline-flex rounded-full border border-riq-border p-1">
          {OS.map((o) => (
            <button key={o.k} data-testid={`script-os-${o.k}`} onClick={() => setKind(o.k)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${kind === o.k ? "bg-neutral-900 text-white" : "text-neutral-600"}`}>{o.l}</button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{os.hint}</p>
        <button data-testid="script-download" disabled={!picked.length} onClick={dl}
          className="mt-3 flex items-center gap-2 rounded-full bg-riq-primary px-5 py-2.5 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform disabled:opacity-50">
          <DownloadSimple size={16} weight="bold" /> Download {os.file}
        </button>

        {kind !== "docker" && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Or copy-paste (inside the worker folder)
              <button data-testid="script-copy" onClick={() => copyText(cmd, "Command copied")} className="flex items-center gap-1 normal-case text-riq-primary"><Copy size={13} /> Copy</button>
            </div>
            <pre data-testid="script-command" className="mt-2 whitespace-pre-wrap break-all rounded-2xl bg-neutral-900 p-4 text-xs text-emerald-200">{cmd}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

function RetailerCard({ r, basketSize, picked, onPick, onScrape, running }) {
  const live = r.status === "live";
  const last = r.last_run;
  const pct = Math.round((r.basket_priced / basketSize) * 100);
  return (
    <div data-testid={`retailer-card-${r.key}`} className={`rounded-2xl border p-4 transition-colors ${picked ? "border-riq-primary bg-riq-primary/5" : "border-riq-border bg-white"}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{r.name}</div>
          <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${live ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {live ? <Lightning size={10} weight="fill" /> : <Desktop size={10} weight="fill" />} {live ? "Live on server" : "Needs worker"}
          </span>
        </div>
        {!live && <input data-testid={`retailer-pick-${r.key}`} type="checkbox" checked={picked} onChange={() => onPick(r.key)} className="mt-1 h-4 w-4 accent-[#6E3FFC]" />}
      </div>
      <div className="mt-3">
        <div className="flex justify-between text-[11px] text-muted-foreground"><span>Basket this month</span><span className="tabular-nums">{r.basket_priced}/{basketSize}</span></div>
        <div className="mt-1 h-1.5 rounded-full bg-neutral-100"><div className="h-full rounded-full bg-riq-primary transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      <div data-testid={`retailer-last-${r.key}`} className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
        {last ? (<>{last.status === "success" ? <CheckCircle size={12} className="text-emerald-600" /> : <WarningCircle size={12} className="text-amber-600" />}
          Last run {last.started_at?.slice(0, 10)} · {last.results_count} products{last.status === "running" ? ` · ${last.progress}/${last.queries_count}` : ""}</>) : "Never run"}
        <span className="ml-auto tabular-nums">{r.products} listed</span>
      </div>
      {live && (
        <button data-testid={`retailer-scrape-${r.key}`} onClick={() => onScrape(r)} disabled={running}
          className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60">
          {running ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} weight="fill" />} {running ? "Scraping basket…" : "Scrape basket now (live)"}
        </button>
      )}
    </div>
  );
}

export const ScraperRetailers = ({ ingest, onRunsChanged }) => {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [dialog, setDialog] = useState(false);
  const pollRef = useRef(null);
  const load = () => api.get("/admin/scraper/overview").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const scrape = async (r) => {
    try {
      const { data: run } = await api.post("/admin/scraper/run", { retailer: r.key, source: "hpi", use_receipts: false, limit: 500 });
      toast.success(`${r.name}: scraping ${run.queries_count} basket items on the server`);
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const { data: s } = await api.get(`/admin/scraper/runs/${run.id}`);
        await load();
        if (s.status !== "running") { clearInterval(pollRef.current); onRunsChanged?.(); toast.success(`${r.name}: ${s.results_count} products scraped`); }
      }, 5000);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start"); }
  };

  if (data === false) return <Card>Could not load retailers.</Card>;
  if (!data) return <Loading />;
  const external = data.retailers.filter((r) => r.status !== "live");
  return (
    <Card data-testid="scraper-retailers">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <div className="font-head text-lg font-bold">Retailers · HPI basket ({data.basket_size} items)</div>
          <p className="text-xs text-muted-foreground">Live retailers scrape from our server with one click. The rest need a worker on a normal internet connection. Tick them and generate a script.
            {data.worker_last ? ` Last worker basket run: ${data.worker_last.at?.slice(0, 10)} (${data.worker_last.total} products).` : " No worker basket run yet."}</p>
        </div>
        <button data-testid="generate-script-btn" onClick={() => setDialog(true)}
          className="flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-riq-primary transition-colors">
          <Code size={16} weight="bold" /> Generate script{picked.length ? ` (${picked.length})` : ""}
        </button>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.retailers.map((r) => (
          <RetailerCard key={r.key} r={r} basketSize={data.basket_size} picked={picked.includes(r.key)}
            onPick={(k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]))}
            onScrape={scrape} running={r.last_run?.status === "running"} />
        ))}
      </div>
      {dialog && <ScriptDialog selected={picked} all={external} ingest={ingest} onClose={() => setDialog(false)} />}
    </Card>
  );
};
